import React, { createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";

const NavigationContext = createContext();

/**
 * NavigationProvider - Provides navigation functionality to the entire app
 * This allows service files like APIService to access React Router navigation
 */
export const NavigationProvider = ({ children }) => {
  const navigate = useNavigate();

  const navigationService = React.useMemo(
    () => ({
      navigate,
      // Add other navigation-related functions as needed
      goToLogin: () => navigate("/login"),
      goToHome: () => navigate("/"),
    }),
    [navigate]
  );

  // Set global navigation service for use by non-React files
  React.useEffect(() => {
    console.log("🔧 NavigationProvider: Setting global navigation service");
    setGlobalNavigationService(navigationService);
    return () => {
      console.log("🔧 NavigationProvider: Clearing global navigation service");
      setGlobalNavigationService(null);
    };
  }, [navigationService]);

  return (
    <NavigationContext.Provider value={navigationService}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Hook to access navigation service from anywhere in the app
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error("useNavigation must be used within a NavigationProvider");
  }
  return context;
};

// Global navigation service - to be set by the provider
let globalNavigationService = null;

/**
 * Set the global navigation service (called by NavigationProvider)
 * This allows non-React files like APIService to access navigation
 */
export const setGlobalNavigationService = (navigationService) => {
  console.log(
    "🔧 setGlobalNavigationService called with:",
    !!navigationService
  );
  globalNavigationService = navigationService;
};

/**
 * Get navigation service for use in non-React files
 * Used by services like APIService that can't use React hooks
 */
export const getGlobalNavigationService = () => {
  console.log(
    "🔍 getGlobalNavigationService called, available:",
    !!globalNavigationService
  );
  if (!globalNavigationService) {
    console.warn(
      "Navigation service not available. Make sure NavigationProvider is set up."
    );
    return null;
  }
  return globalNavigationService;
};

export default NavigationContext;
